import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Scissors, CheckCircle2, Printer, XCircle, EyeOff } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  planned: "border-blue-200 bg-blue-50 text-blue-700",
  confirmed: "border-violet-200 bg-violet-50 text-violet-700",
  in_progress: "border-amber-200 bg-amber-50 text-amber-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-rose-200 bg-rose-50 text-rose-700",
};

// Row tinting per status — applied as full-row background on the schedule table
const ROW_BG = {
  planned: "bg-blue-50/40 hover:bg-blue-50",
  confirmed: "bg-violet-50/40 hover:bg-violet-50",
  in_progress: "bg-amber-50/40 hover:bg-amber-50",
  completed: "bg-emerald-50/50 hover:bg-emerald-50",
  cancelled: "bg-rose-50/40 hover:bg-rose-50 line-through",
};

export default function OtSchedule() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [patientQ, setPatientQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [surgeryMaster, setSurgeryMaster] = useState([]);
  const [form, setForm] = useState({ patient_id: "", patient_name: "", surgery_name: "", eye: "OD", surgeon_id: "", scheduled_date: "", scheduled_time: "", notes: "" });

  const load = async () => { const { data } = await api.get("/ot/bookings"); setItems(data.items); };
  useEffect(() => {
    load();
    api.get("/hospital/doctors").then(({ data }) => setDoctors(data.items));
    Promise.all([
      api.get("/clinical-masters/surgeries").catch(() => ({ data: { items: [] } })),
      api.get("/clinical-masters/procedures").catch(() => ({ data: { items: [] } })),
    ]).then(([s, p]) => {
      const all = [...(s.data.items || []), ...(p.data.items || [])];
      // de-dupe by name
      const seen = new Set();
      const merged = [];
      for (const it of all) {
        const key = (it.name || "").trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push(it);
      }
      merged.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setSurgeryMaster(merged);
    });
  }, []);

  const search = async (q) => {
    setPatientQ(q);
    if (q.length < 2) { setPatients([]); return; }
    const { data } = await api.get("/patients", { params: { q, limit: 8 } });
    setPatients(data.items);
  };

  const save = async () => {
    if (!form.patient_id || !form.surgery_name || !form.scheduled_date) { toast.error("Patient, surgery name & date required"); return; }
    try {
      await api.post("/ot/bookings", { patient_id: form.patient_id, surgery_name: form.surgery_name, eye: form.eye, surgeon_id: form.surgeon_id || null, scheduled_date: form.scheduled_date, scheduled_time: form.scheduled_time, notes: form.notes });
      toast.success("OT booked"); setOpen(false); load();
      setForm({ patient_id: "", patient_name: "", surgery_name: "", eye: "OD", surgeon_id: "", scheduled_date: "", scheduled_time: "", notes: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const setStatus = async (id, s) => { await api.patch(`/ot/bookings/${id}/status`, { status: s }); load(); };

  const cancelBooking = async (b) => {
    if (b.status === "cancelled" || b.status === "completed") return;
    if (!window.confirm(`Cancel OT booking for ${b.patient_name} — ${b.surgery_name} (${b.eye})?\n\nThis can be undone by re-confirming the booking.`)) return;
    try {
      await api.patch(`/ot/bookings/${b.id}/status`, { status: "cancelled" });
      toast.success("OT booking cancelled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const visibleItems = showCancelled ? items : items.filter((b) => b.status !== "cancelled");
  const cancelledCount = items.filter((b) => b.status === "cancelled").length;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-label">Operation theatre</div>
          <h1 className="font-heading text-3xl font-semibold">OT schedule</h1>
          <p className="mt-1 text-sm text-ink-muted">WHO surgical safety + NABH consent · IOL batch tracking · per-branch register.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-700" data-testid="ot-new-btn"><Plus size={14} className="mr-2" />Book surgery</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">New OT booking</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div>
                <Label>Patient</Label>
                {form.patient_id ? (
                  <div className="flex items-center justify-between rounded-md border border-border p-2"><div>{form.patient_name}</div><Button size="sm" variant="outline" onClick={() => setForm({...form, patient_id: "", patient_name: ""})}>Change</Button></div>
                ) : (
                  <>
                    <Input value={patientQ} onChange={e => search(e.target.value)} placeholder="Search patient…" />
                    {patients.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-auto rounded-md border border-border">
                        {patients.map(p => (
                          <button key={p.id} className="block w-full border-b border-border px-3 py-2 text-left text-sm hover:bg-secondary" onClick={() => { setForm(f => ({...f, patient_id: p.id, patient_name: p.name})); setPatients([]); setPatientQ(""); }}>
                            <div className="font-medium">{p.name}</div><div className="text-xs text-ink-muted">{p.uhid} · {p.mobile}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <Label>Surgery / Procedure name</Label>
                <Input
                  value={form.surgery_name}
                  onChange={e => setForm({ ...form, surgery_name: e.target.value })}
                  placeholder="Type or pick from master — e.g. Phaco + IOL"
                  list="ot-surgery-master"
                  data-testid="ot-surgery-input"
                />
                <datalist id="ot-surgery-master">
                  {surgeryMaster.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.category ? `${s.category}` : ""}{s.estimated_cost ? ` · ₹${s.estimated_cost}` : ""}{s.duration_min ? ` · ${s.duration_min} min` : ""}
                    </option>
                  ))}
                </datalist>
                {surgeryMaster.length > 0 && (
                  <div className="mt-1 text-[10px] text-ink-muted">
                    {surgeryMaster.length} surgeries/procedures available — start typing to filter.
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Eye</Label>
                  <Select value={form.eye} onValueChange={v => setForm({...form, eye: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="OD">OD (Right)</SelectItem><SelectItem value="OS">OS (Left)</SelectItem><SelectItem value="OU">OU (Both)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Surgeon</Label>
                  <Select value={form.surgeon_id} onValueChange={v => setForm({...form, surgeon_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})} data-testid="ot-date-input" /></div>
                <div><Label>Time</Label><Input type="time" value={form.scheduled_time} onChange={e => setForm({...form, scheduled_time: e.target.value})} /></div>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="ot-save-btn">Book</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border bg-secondary/50 px-3 py-2 text-[11px] text-ink-muted">
          <div>Showing <b className="text-ink">{visibleItems.length}</b> bookings{cancelledCount > 0 && !showCancelled && <span> · <b className="text-rose-700">{cancelledCount}</b> cancelled hidden</span>}</div>
          {cancelledCount > 0 && (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
              onClick={() => setShowCancelled((v) => !v)}
              data-testid="ot-toggle-cancelled"
            >
              <EyeOff size={11} /> {showCancelled ? "Hide cancelled" : "Show cancelled"}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Patient</th><th className="px-3 py-2 text-left">Surgery</th><th className="px-3 py-2 text-left">Eye</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-right">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleItems.length === 0 && <tr><td colSpan="6" className="px-3 py-10 text-center text-ink-muted">No bookings yet.</td></tr>}
              {visibleItems.map(b => (
                <tr key={b.id} className={`${ROW_BG[b.status] || ""} transition`} data-testid={`ot-row-${b.id}`}>
                  <td className="px-3 py-2">{b.scheduled_date} {b.scheduled_time}</td>
                  <td className="px-3 py-2"><div className="font-medium">{b.patient_name}</div><div className="text-[11px] font-mono text-ink-muted">{b.patient_uhid}</div></td>
                  <td className="px-3 py-2">{b.surgery_name}</td>
                  <td className="px-3 py-2">{b.eye}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={STATUS[b.status] || ""}>{b.status.replace("_"," ")}</Badge></td>
                  <td className="px-3 py-2 space-x-2 text-right">
                    {b.status !== "completed" && b.status !== "cancelled" && <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "confirmed")} data-testid={`ot-confirm-${b.id}`}>Confirm</Button>}
                    {b.status !== "completed" && b.status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => cancelBooking(b)} data-testid={`ot-cancel-${b.id}`}>
                        <XCircle size={12} className="mr-1" /> Cancel
                      </Button>
                    )}
                    {b.status === "completed" && (
                      <a href={`/print/ot/${b.id}`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" data-testid={`ot-print-${b.id}`}><Printer size={12} className="mr-1" />Print</Button>
                      </a>
                    )}
                    {b.status === "cancelled"
                      ? <span className="inline-block rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700">Cancelled — cannot open</span>
                      : <Link to={`/app/ot/${b.id}`}><Button size="sm" className="bg-brand hover:bg-brand-700"><Scissors size={12} className="mr-1" />Open</Button></Link>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
